import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Snackbar,
  Typography,
  useTheme,
} from "@mui/material";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import PaymentsIcon from "@mui/icons-material/Payments";
import { Link as RouterLink } from "react-router-dom";
import useRequest from "../../api/useRequest";
import { DBRequest } from "../../api/dbRequest";
import { isTenantRequiredError, TENANT_REQUIRED_MESSAGE } from "../../api/creditInfo";
import CardContainer from "../../components/cards/CardContainer";
import CardGrid from "../../components/cards/CardGrid";
import SubscriptionTerms from "../../components/SubscriptionTerms";
import { tokens } from "../../theme/theme";

const subscriptionPlans = [
  {
    name: "Basic",
    key: "basic_monthly",
    description: "Monthly Ziggurat Sewer subscription",
  },
  
  /*{
    name: "Starter",
    key: "starter_monthly",
    description: "Monthly Ziggurat Sewer subscription",
  },
  {
    name: "Pro",
    key: "pro_monthly",
    description: "Monthly Ziggurat Sewer subscription",
  },
  */
];

const creditPacks = [
  {
    name: "1.000 Credits",
    key: "credits_1000",
    credits: 1000,
    description: "One-time credit purchase",
  },
  {
    name: "10.000 Credits",
    key: "credits_10000",
    credits: 10000,
    description: "One-time credit purchase",
  },
  {
    name: "100.000 Credits",
    key: "credits_100000",
    credits: 100000,
    description: "One-time credit purchase",
  },
];

const formatCredits = (value, language) =>
  Number(value || 0).toLocaleString(language === "de" ? "de-DE" : "en-US");

export const formatSubscriptionEnd = (value, language) =>
  value
    ? new Intl.DateTimeFormat(language === "de" ? "de-DE" : "en-US", {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(value))
    : "–";

const activeSubscriptionStatuses = new Set(["active", "trialing"]);

const isActivePlan = (subscription, plan) =>
  activeSubscriptionStatuses.has(subscription?.status) &&
  (subscription?.key === plan.key ||
    subscription?.name?.toLowerCase() === plan.name.toLowerCase());

const Billing = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { apiData, isLoading, serverError } = useRequest({ path: "credit_info" });
  const hasTenant = apiData?.tenant_id !== null && apiData?.tenant_id !== undefined;
  const tenantRequired = isTenantRequiredError(serverError?.error);
  const creditError = Boolean(serverError) && !tenantRequired;
  const hasNoTenant = !isLoading && (tenantRequired || (!serverError && !hasTenant));
  const billingActionsDisabled = isLoading || Boolean(serverError) || !hasTenant;
  const {
    apiData: billingData,
    isLoading: isSubscriptionLoading,
    setApiData: setBillingData,
  } = useRequest({
    path: "billing-history?page=1&page_size=1",
    enabled: hasTenant,
  });
  const language = (i18n.resolvedLanguage || i18n.language || "en").split("-")[0];

  const [checkoutKey, setCheckoutKey] = useState(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);
  const [message, setMessage] = useState(null);

  const creditSummary = useMemo(
    () => ({
      total: apiData?.credit_balance || 0,
      monthly: apiData?.monthly_credit_balance || 0,
      purchased: apiData?.purchased_credit_balance || 0,
    }),
    [apiData]
  );

  async function createCheckoutSession(checkoutType, key) {
    return new Promise((resolve, reject) => {
      DBRequest({
        path: "create-checkout-session",
        method: "post",
        data: {
          checkout_type: checkoutType,
          key,
        },
        onResponse: resolve,
        onError: reject,
        onLoading: () => {},
      });
    });
  }

  async function handleCheckout(checkoutType, key) {
    if (billingActionsDisabled) return;

    setCheckoutKey(key);
    try {
      const session = await createCheckoutSession(checkoutType, key);
      if (!session?.url) {
        throw new Error(t("Checkout URL is missing from the API response."));
      }
      window.location.assign(session.url);
    } catch (error) {
      console.error("Checkout failed:", error);
      setMessage({
        severity: "error",
        text:
          error?.response?.data?.message ||
          error?.message ||
          t("Checkout could not be started."),
      });
    } finally {
      setCheckoutKey(null);
    }
  }

  async function handleCancelSubscription() {
    if (billingActionsDisabled) return;

    setIsCanceling(true);
    try {
      const response = await new Promise((resolve, reject) => {
        DBRequest({
          path: "subscriptions/cancel",
          method: "post",
          onResponse: resolve,
          onError: reject,
          onLoading: () => {},
        });
      });
      setBillingData((current) => ({
        ...(current || {}),
        current_subscription: response.current_subscription,
      }));
      setCancelDialogOpen(false);
      setMessage({
        severity: "success",
        text: t("Your subscription cancellation has been scheduled. A confirmation email will follow."),
      });
    } catch (error) {
      console.error("Subscription cancellation failed:", error);
      setMessage({
        severity: "error",
        text:
          error?.response?.data?.message ||
          error?.message ||
          t("Subscription cancellation could not be scheduled."),
      });
    } finally {
      setIsCanceling(false);
    }
  }

  return (
    <Box className="flex flex-col gap-6">
      <Box>
        <Typography variant="h2">{t("Credits & Billing")}</Typography>
        <Typography variant="h4" sx={{ color: colors.contrast[700], mt: 1 }}>
          {t("Buy credits or start a monthly subscription.")}
        </Typography>
        <Button
          href="/purchase-history"
          disabled={billingActionsDisabled}
          sx={{ mt: 2 }}
        >
          {t("View purchase history")}
        </Button>
      </Box>

      {hasNoTenant && (
        <Alert
          severity="info"
          action={
            <Button
              color="inherit"
              component={RouterLink}
              to="/team"
            >
              {t("Go to team")}
            </Button>
          }
        >
          {tenantRequired ? TENANT_REQUIRED_MESSAGE : t("Create an organization first to use credits and billing.")}
        </Alert>
      )}

      <CardGrid>
        <CreditMetric
          label={t("Total")}
          value={
            isLoading
              ? "..."
              : creditError
              ? t("Error")
              : formatCredits(creditSummary.total, language)
          }
        />
        <CreditMetric
          label={t("Monthly")}
          value={
            isLoading
              ? "..."
              : creditError
              ? t("Error")
              : formatCredits(creditSummary.monthly, language)
          }
        />
        <CreditMetric
          label={t("Purchased")}
          value={
            isLoading
              ? "..."
              : creditError
              ? t("Error")
              : formatCredits(creditSummary.purchased, language)
          }
        />
      </CardGrid>

      <PurchaseSection
        title={t("Buy a subscription")}
        Icon={EventRepeatIcon}
        items={subscriptionPlans}
        checkoutType="subscription"
        checkoutKey={checkoutKey}
        onCheckout={handleCheckout}
        activeSubscription={billingData?.current_subscription}
        isSubscriptionLoading={isSubscriptionLoading}
        isCanceling={isCanceling}
        actionsDisabled={billingActionsDisabled}
        onCancel={() => setCancelDialogOpen(true)}
        language={language}
        t={t}
      />

      <Dialog
        open={cancelDialogOpen}
        onClose={isCanceling ? undefined : () => setCancelDialogOpen(false)}
        aria-labelledby="cancel-subscription-title"
      >
        <DialogTitle id="cancel-subscription-title">
          {t("Cancel subscription?")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("Your Basic subscription remains active until {{date}} and will not renew afterwards.", {
              date: formatSubscriptionEnd(
                billingData?.current_subscription?.cancel_at ||
                  billingData?.current_subscription?.earliest_cancellation_at ||
                  billingData?.current_subscription?.current_period_end,
                language
              ),
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={isCanceling || billingActionsDisabled}
            onClick={() => setCancelDialogOpen(false)}
          >
            {t("Keep subscription")}
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={isCanceling || billingActionsDisabled}
            onClick={handleCancelSubscription}
            startIcon={isCanceling ? <CircularProgress color="inherit" size={16} /> : null}
          >
            {isCanceling ? t("Canceling...") : t("Confirm cancellation")}
          </Button>
        </DialogActions>
      </Dialog>

      <PurchaseSection
        title={t("Buy credits")}
        Icon={PaymentsIcon}
        items={creditPacks}
        checkoutType="credit_pack"
        checkoutKey={checkoutKey}
        onCheckout={handleCheckout}
        actionsDisabled={billingActionsDisabled}
        language={language}
        t={t}
      />

      <Snackbar
        open={Boolean(message)}
        autoHideDuration={7000}
        onClose={() => setMessage(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        {message && (
          <Alert severity={message.severity} onClose={() => setMessage(null)}>
            {message.text}
          </Alert>
        )}
      </Snackbar>
    </Box>
  );
};

const CreditMetric = ({ label, value }) => {
  const { t } = useTranslation();

  return (
    <CardContainer className="items-start p-4" sx={{ minHeight: 120 }}>
      <Typography variant="h6">{label}</Typography>
      <Typography variant="h2">{value}</Typography>
      <Typography variant="h5">{t("Credits")}</Typography>
    </CardContainer>
  );
};

const PurchaseSection = ({
  title,
  Icon,
  items,
  checkoutType,
  checkoutKey,
  onCheckout,
  activeSubscription,
  isSubscriptionLoading = false,
  isCanceling = false,
  actionsDisabled = false,
  onCancel,
  language,
  t,
}) => (
  <Box className="flex flex-col gap-4">
    <Box className="flex flex-row gap-2 items-center">
      <Icon />
      <Typography variant="h3">{title}</Typography>
    </Box>
    <CardGrid>
      {items.map((item) => (
        <PurchaseCard
          item={item}
          checkoutType={checkoutType}
          isLoading={checkoutKey === item.key}
          isActive={
            checkoutType === "subscription" &&
            isActivePlan(activeSubscription, item)
          }
          cancellationScheduled={Boolean(
            activeSubscription?.cancellation_scheduled || activeSubscription?.cancel_at || activeSubscription?.cancel_at_period_end
          )}
          subscriptionEnd={activeSubscription?.cancel_at || activeSubscription?.current_period_end}
          activeSubscription={activeSubscription}
          isCanceling={isCanceling}
          onCancel={onCancel}
          disabled={
            actionsDisabled ||
            Boolean(checkoutKey) ||
            (checkoutType === "subscription" && isSubscriptionLoading)
          }
          onCheckout={onCheckout}
          language={language}
          t={t}
          key={item.key}
        />
      ))}
    </CardGrid>
  </Box>
);

const PurchaseCard = ({
  item,
  checkoutType,
  isLoading,
  isActive,
  cancellationScheduled,
  subscriptionEnd,
  activeSubscription,
  isCanceling,
  disabled,
  onCheckout,
  onCancel,
  language,
  t,
}) => (
  <CardContainer
    className="items-start justify-between p-4"
    sx={{ minHeight: 210 }}
  >
    <Box className="flex flex-col gap-2 w-full">
      <Box className="flex flex-row justify-between items-start gap-3 w-full">
        <Box>
          <Typography variant="h3">
            {item.credits
              ? `${formatCredits(item.credits, language)} ${t("Credits")}`
              : item.name}
          </Typography>
          <Typography variant="h5">{t(item.description)}</Typography>
        </Box>
        {isActive ? (
          <Chip
            color={cancellationScheduled ? "warning" : "success"}
            size="small"
            label={t(cancellationScheduled ? "Cancellation scheduled" : "Active subscription")}
          />
        ) : (
          <CreditCardIcon />
        )}
      </Box>
      <Divider className="w-full" />
      {checkoutType === "subscription" && (
        <SubscriptionTerms
          subscription={isActive ? activeSubscription : null}
          newPurchase={!isActive}
          language={language}
        />
      )}
      {item.credits && (
        <Typography variant="h4">
          {formatCredits(item.credits, language)} {t("Credits")}
        </Typography>
      )}
      {isActive && cancellationScheduled && (
        <Typography variant="h5">
          {t("Ends on")}: {formatSubscriptionEnd(subscriptionEnd, language)}
        </Typography>
      )}
    </Box>
    {isActive ? (
      <Button
        variant="contained"
        color={cancellationScheduled ? "secondary" : "error"}
        disabled={disabled || cancellationScheduled || isCanceling}
        onClick={onCancel}
      >
        {cancellationScheduled ? t("Cancellation scheduled") : t("Cancel subscription")}
      </Button>
    ) : (
      <Button
        variant="contained"
        color="secondary"
        disabled={disabled}
        onClick={() => onCheckout(checkoutType, item.key)}
        startIcon={isLoading ? <CircularProgress color="inherit" size={16} /> : null}
      >
        {isLoading ? t("Redirecting...") : t("Buy")}
      </Button>
    )}
  </CardContainer>
);

export default Billing;
